<template>
  <label :class="classes">
    <input
      class="toggle-button__input"
      type="checkbox"
      v-model="checkedValue"
      @change="onChange($event)"
    />
    <component
      v-if="iconLeft"
      :is="iconLeft"
      :extra-classes="extraIconClasses"
    />

    {{ text }}
  </label>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'

  const props = defineProps({
    text: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'default',
    },
    isChecked: {
      type: Boolean,
      default: false,
      required: true,
    },
    iconLeft: {
      type: String,
    },
    iconClasses: {
      type: Array<string>,
      default: function () {
        return []
      },
    },
    extraClasses: {
      type: Array<string>,
      default: function () {
        return []
      },
    },
  })

  const emits = defineEmits<{
    toggle: [value: boolean]
  }>()

  const onChange = (event: Event) => emits("toggle", (event.target as HTMLInputElement).checked);

  const checkedValue = ref(props.isChecked)
  // Since we're just using the prop to initialize a ref for the checked value,
  // we need to watch for changes of the prop to automatically update the value
  // accordingly.
  // Otherwise checkedValue would not get updated when the parent component
  // updates the isChecked prop.
  watch(
    () => props.isChecked,
    (newValue) => {
      checkedValue.value = newValue
    }
  )

  defineExpose({
    checkedValue,
  })

  const classes = computed(() => {
    let classes = ['toggle-button', `toggle-button--${props.type}`]
    if (checkedValue.value) {
      classes.push('toggle-button--checked')
    }
    return classes.concat(props.extraClasses).join(' ')
  })

  const extraIconClasses = computed(() => {
    let classes = ['toggle-button__icon-left']
    return classes.concat(props.iconClasses)
  })

</script>

<style lang="scss">
  .toggle-button {
    $default-color: $grey-dark;
    transition: $transition-base;
    border: 1px solid $grey-lighter;
    border-radius: $border-radius;
    padding: $spacer01 $spacer05;
    position: relative;
    cursor: pointer;
    color: $default-color;
    margin-right: $spacer05;

    &:last-child {
      margin-right: 0;
    }

    .icon {
      width: 0.8em;
      vertical-align: middle;
    }

    &__icon-left {
      margin-right: $spacer025;
    }

    &--checked {
      color: $white;
    }

    &--default {
      &.toggle-button--checked {
        background: $default-color;
        border-color: $default-color;
      }
    }

    &--prioritised {
      &.toggle-button--checked {
        background: $error-color;
        border-color: $error-color;
      }
    }

    &__input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
    }
  }
</style>
